import React, { useMemo, useState } from "react";
import { Alert, message } from "antd";
import dayjs from "dayjs";
import { createBillingPolicyApi } from "../../../../../services/billingPolicies.services";
import { IconCalendar, IconChevronRight, IconInfo, MODE_ICONS } from "./icons";
import RuleEditor, { Ruler } from "./RuleEditor";
import TestPanel from "./TestPanel";
import {
  MODE_BLURB,
  MODE_FIXED_NOTE,
  MODE_ICON,
  MODE_LABELS,
  MODE_ORDER,
  MODE_TABLES,
  STANDARD,
  STANDARD_TIERS,
  apiError,
  countConfigurationErrors,
  defaultConfiguration,
  describeTiers,
  formatDateTime,
  normalizeConfiguration,
  tierErrors,
} from "./policyUtils";
import styles from "./BillingPolicies.module.css";

const FIELD = "YYYY-MM-DDTHH:mm";

const tomorrow = () => dayjs().add(1, "day").startOf("day");


/* 1 · Billing mode. */
const ModeCards = ({ mode, onChange }) => (
  <div className={styles.modes}>
    {MODE_ORDER.map((key) => {
      const Icon = MODE_ICONS[MODE_ICON[key]];
      return (
        <button
          key={key}
          type="button"
          className={`${styles.mode} ${mode === key ? styles.on : ""}`}
          onClick={() => onChange(key)}
        >
          <span className={styles.radio} />
          <span className={styles.mI}>
            <Icon size={17} />
          </span>
          <b>{MODE_LABELS[key]}</b>
          <small>{MODE_BLURB[key]}</small>
        </button>
      );
    })}
  </div>
);

/* 2 · Rules. Standard is fixed across Curengo, so it is shown on the scale
   and nothing else; the other modes get a tab per table. */
const RulesSection = ({ mode, configuration, ruleTab, onRuleTab, onChange }) => {
  if (mode === STANDARD) {
    return (
      <>
        <p className={styles.fixed}>
          Standard is fixed across Curengo, so there is nothing to set — the first 24 hours are
          always a full day, then leftover hours step up like this:
        </p>
        <Ruler tiers={STANDARD_TIERS} />
        <div className={styles.plain}>
          <IconInfo size={15} />
          <span>{describeTiers(STANDARD_TIERS)}</span>
        </div>
      </>
    );
  }

  const tables = MODE_TABLES[mode];
  const active = tables.find((t) => t.key === ruleTab) || tables[0];

  return (
    <>
      {tables.length > 1 && (
        <div className={styles.rTabs}>
          {tables.map((table) => {
            const bad = Object.keys(tierErrors(configuration[table.key])).length;
            return (
              <button
                key={table.key}
                type="button"
                className={active.key === table.key ? styles.on : ""}
                onClick={() => onRuleTab(table.key)}
              >
                {table.label}
                {!!bad && <span className={styles.err} />}
              </button>
            );
          })}
        </div>
      )}

      <p className={styles.rIntro}>
        {active.help} · {MODE_FIXED_NOTE[mode]}
      </p>

      <RuleEditor
        tiers={configuration[active.key]}
        onChange={(tiers) => onChange({ ...configuration, [active.key]: tiers })}
      />
    </>
  );
};

const PolicyConfigure = ({ group, onBack, onScheduled }) => {
  const { facility, current, scheduled } = group;
  const base = scheduled || current;
  const baseMode = MODE_ORDER.includes(base?.billing_mode) ? base.billing_mode : STANDARD;

  const [mode, setMode] = useState(baseMode);
  const [configuration, setConfiguration] = useState(() =>
    normalizeConfiguration(baseMode, base?.configuration)
  );
  const [ruleTab, setRuleTab] = useState(() => MODE_TABLES[baseMode][0]?.key);
  const [effectiveFrom, setEffectiveFrom] = useState(() => tomorrow().format(FIELD));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const changeMode = (next) => {
    setMode(next);
    setRuleTab(MODE_TABLES[next][0]?.key);
    setConfiguration(
      baseMode === next
        ? normalizeConfiguration(next, base.configuration)
        : defaultConfiguration(next)
    );
  };

  const errorCount = countConfigurationErrors(mode, configuration);
  const parsedFrom = useMemo(() => {
    const value = dayjs(effectiveFrom);
    return value.isValid() ? value : null;
  }, [effectiveFrom]);

  const fromError = !parsedFrom
    ? "Pick a date and time"
    : !parsedFrom.isAfter(dayjs())
    ? "Must be in the future — a policy only applies to admissions from then on"
    : "";

  const canSchedule = !errorCount && !fromError;

  const why = errorCount
    ? `Fix the ${errorCount} highlighted step${errorCount === 1 ? "" : "s"} first.`
    : fromError;

  const schedule = async () => {
    if (!canSchedule) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await createBillingPolicyApi(facility.id, {
        billing_mode: mode,
        configuration: mode === STANDARD ? {} : configuration,
        effective_from: parsedFrom.second(0).millisecond(0).format(),
      });
      const version = data?.data?.version;
      message.success(
        `${version ? `v${version}` : "Policy"} scheduled for ${facility.name} · from ${parsedFrom.format(
          "DD MMM YYYY, hh:mm A"
        )}${scheduled ? ` · replaces v${scheduled.version}` : ""}`
      );
      onScheduled(facility.id);
    } catch (e) {
      setSaveError(apiError(e, "Couldn't schedule the billing policy"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.cfgShell}>
      <div className={styles.crumb}>
        <button type="button" onClick={onBack}>
          Billing policies
        </button>
        <IconChevronRight size={12} />
        <span>{facility.name}</span>
      </div>

      <div className={styles.cHead}>
        <div>
          <h3>New billing policy</h3>
          <p>
            {facility.name} · Choose how days are counted, check the result on sample stays, then
            schedule it.
          </p>
        </div>
        <span className={styles.sp} />
        <div className={styles.nowRow}>
          <div className={styles.nowBox}>
            <span>In force now</span>
            <b>
              {current ? MODE_LABELS[current.billing_mode] : "No policy yet"}
              {current && <span className={styles.vchip}>v{current.version}</span>}
            </b>
          </div>
          {scheduled && (
            <div className={`${styles.nowBox} ${styles.sched}`}>
              <span>Scheduled</span>
              <b>
                {MODE_LABELS[scheduled.billing_mode]}
                <span className={styles.vchip}>v{scheduled.version}</span>
              </b>
            </div>
          )}
        </div>
      </div>

      <div className={styles.cGrid}>
        <div className={styles.card}>
          <div className={styles.sec}>
            <div className={styles.secH}>
              <span className={styles.num}>1</span>
              <h4>Billing mode</h4>
            </div>
            <ModeCards mode={mode} onChange={changeMode} />
          </div>

          <div className={styles.sec}>
            <div className={styles.secH}>
              <span className={styles.num}>2</span>
              <h4>Rules</h4>
              <span className={styles.hint}>
                {mode === STANDARD ? "Fixed" : "Steps are checked top to bottom"}
              </span>
            </div>
            <RulesSection
              mode={mode}
              configuration={configuration}
              ruleTab={ruleTab}
              onRuleTab={setRuleTab}
              onChange={setConfiguration}
            />
          </div>

          <div className={styles.sec}>
            <div className={styles.secH}>
              <span className={styles.num}>3</span>
              <h4>Effective from</h4>
            </div>
            <div className={styles.eff}>
              <div>
                <input
                  type="datetime-local"
                  className={`${styles.fi} ${fromError ? styles.bad : ""}`}
                  value={effectiveFrom}
                  aria-label="Effective from"
                  onChange={(event) => setEffectiveFrom(event.target.value)}
                />
                {fromError && <div className={styles.effErr}>{fromError}</div>}
              </div>
              <div className={styles.effNote}>
                Admissions whose first bed is assigned on or after this time use this policy.
                Admissions already in progress keep the policy they started on.
                {scheduled && (
                  <>
                    <br />
                    <b style={{ color: "var(--cg-warning-700)" }}>
                      Scheduling replaces v{scheduled.version} ({MODE_LABELS[scheduled.billing_mode]},
                      from {formatDateTime(scheduled.effective_from)}).
                    </b>
                  </>
                )}
              </div>
            </div>

            {saveError && (
              <Alert className={styles.alert} type="error" showIcon message={saveError} />
            )}
          </div>
        </div>

        <TestPanel
          facility={facility}
          billingMode={mode}
          configuration={mode === STANDARD ? {} : configuration}
          currentPolicy={current}
        />
      </div>

      <div className={styles.actBar}>
        <div className={styles.actSum}>
          <b>{MODE_LABELS[mode]}</b>
          <span>
            {parsedFrom
              ? `From ${parsedFrom.format("DD MMM YYYY, hh:mm A")}`
              : "Pick when it starts"}
          </span>
        </div>
        <span className={styles.sp} />
        {why && (
          <span className={styles.actWarn}>
            <IconInfo size={15} />
            {why}
          </span>
        )}
        <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onBack}>
          Cancel
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={!canSchedule || saving}
          onClick={schedule}
        >
          <IconCalendar size={16} />
          {saving ? "Scheduling…" : "Schedule policy"}
        </button>
      </div>
    </div>
  );
};

export default PolicyConfigure;
